import React from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Tipos base
// ─────────────────────────────────────────────────────────────────────────────

export interface IconProps {
  size?: number;
  color?: string;
  className?: string;
  strokeWidth?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Wrapper — íconos ilustrativos (viewBox 128, estilo propio del usuario)
// ─────────────────────────────────────────────────────────────────────────────

function IllustIcon({
  size = 24,
  color = "currentColor",
  className = "",
  strokeWidth,
  viewBox = "0 0 198.78 194.26",
  children,
}: IconProps & { children: React.ReactNode; viewBox?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill={color}
      stroke="none"
      className={className}
    >
      {children}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wrapper — íconos lineales (viewBox 24, estilo Lucide)
// ─────────────────────────────────────────────────────────────────────────────

function LineIcon({
  size = 24,
  color = "currentColor",
  className = "",
  strokeWidth = 2,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {children}
    </svg>
  );
}

// =============================================================================
// AGRO / GANADO  —  SVGs originales del usuario (viewBox 128)
// =============================================================================

/** Cara de vaca con cuernos y hocico */
export function AnimalCow({ size = 24, color = "currentColor", className = "" }: IconProps) {
  return (
    <IllustIcon size={size} color={color} className={className}>
      <path d="M155.98,91.95c2.59,14.94-2.02,28.44-10.39,40.42,3.71,8.63,6.23,18.14,1.26,27.98-3.35,6.63-11.55,14.73-21.41,15.18-16.88.78-33.5.51-50.68.38-7.36-.06-13.99-4.12-19.28-8.6-8.8-10.05-9.6-23.81-2.84-34.94-8.45-11.94-12.74-25.71-9.91-40.5-14.16,1.52-27.51-5.99-31.89-19.74,8.88-12.29,29.96-15.15,41.63-4.2l6.86-7.06c-7.84-12.97-4.5-27.91,7.21-37.2,1.54-.23,3.79-.23,4.84.31,4.21,2.19-7.06,15.83,9.88,25.67,11.55-2.99,23.41-3.3,35.55-.26,8.69-2.81,13.57-11.71,9.77-20.49-.17-1.73.68-4.78,2.02-5.38,6.43-2.87,24.08,18.55,11.2,37.15l6.24,7.42c9.9-9.06,23.3-8.74,34.33-1.58,3.93,2.55,6.9,6.4,3.98,11.21-5.77,9.48-15.96,15.35-28.35,14.24ZM74.35,51.89c-6.6-4.98-8.49-10.65-8.68-18.27-6.19,6.76-5.21,16.27-1.3,23.71l9.99-5.44ZM134.95,57.41c5.1-8.2,4.53-16.87-.8-23.64-.07,7.82-3.07,13.16-8.97,18.38l9.77,5.25ZM122.39,119.88c7.21.42,13.04,3.17,18.71,7.66,7.96-10.43,11.2-23.35,8.4-36.11-6.77-30.81-34.8-42.43-64.71-36.43-14.21,2.85-25.25,11.6-31.42,24.68-7.35,15.57-6.82,33.39,3.4,47.86,5.43-4.23,11.34-7.03,17.97-7.64l47.66-.02ZM44.23,85.46l4.96-11.72c-7.59-9.35-22.37-8.25-31.37-.35,5.36,9.82,16.25,14.15,26.4,12.07ZM154.49,85.72c10.78,1.68,20.75-3.13,25.27-11.98-8.98-8.13-21.97-9.28-30.12-.5l4.84,12.48ZM124.91,169.73c11.57-2.7,18.74-11.33,18.49-22.26-.22-9.75-7.56-20.39-19.04-20.98-16.94-.87-34.19-.93-51.1.06-11.68.68-19.02,11.96-18.93,21.75.1,11.24,8.16,19.56,19.69,21.53l50.9-.1Z" />
      <path d="M70.92,95.39c3.1-1.25,6.63.92,7.53,3.52,1.03,2.99-.43,6.55-3.6,7.71-2.48.91-5.98-.41-7.28-2.81-1.49-2.77-.67-6.81,3.34-8.42Z" />
      <path d="M128.6,106.07c-2.86,1.78-6.01.83-7.55-.88s-1.91-4.85-.82-7.09c1-2.07,4.48-4.04,7.17-2.72,1.56.77,3.17,3.01,3.5,4.35.39,1.58-.71,5.35-2.3,6.34Z" />
      <path d="M83.95,147.45l-9.47.08c-1.16.01-3.26-1.89-3.29-2.96s1.83-3.22,2.99-3.21l10,.11c1.03.01,2.85,2.1,2.94,3.12s-2.03,2.85-3.17,2.86Z" />
      <path d="M124.22,147.35l-10.76.21c-.78.02-2.5-1.5-2.9-2.19-.49-.87,1.33-3.57,2.37-3.59l10.64-.26c.67-.02,2.16,1.51,2.62,2.01.61.67-.92,3.81-1.97,3.83Z" />
    </IllustIcon>
  );
}

/** Cara de toro con cuernos grandes y expresión seria */
export function AnimalBull({ size = 24, color = "currentColor", className = "" }: IconProps) {
  return (
    <IllustIcon size={size} color={color} className={className}>
      <path d="M183.3,77.27c3.54,1.79,3.67,6.32,1.85,9.16-6.34,9.94-16.65,15.71-29.16,14.27,2.16,14.77-1.76,28.26-10.8,40.15,6.73,11.74,5.92,26.77-4.69,35.96-3.28,2.84-9.54,6.61-14.09,6.61l-52.92.04c-7.91,0-14.8-5.88-19.76-11.05-6.87-9.77-7.07-21.52-.8-31.84-8.28-12.14-12.16-25.74-9.75-40.09-13.84,1.6-24.51-4.78-30.71-16.1-.09-2.58.44-4.52,1.69-6.9l14.41-7.02c-21.73-15.67-8.98-39.44,5.7-58.54.85-1.1,4.15-1.23,4.98-.39,3.36,3.42-5.78,14.3-.93,29.46,1.63,5.11,7.41,7.9,12.28,9.51l26.31,8.69c15.13-5.3,31.5-4.85,46.37.49l16.2-5.88,13.58-4.27c12.39-3.9,10.89-21.3,6.67-34.78-.39-1.23,1.32-3.75,2.45-3.95,1.32-.23,4,1.02,4.81,2.28,12.96,20.11,25.34,40.53,3.52,57.69l12.8,6.49ZM53.5,76.41l15.65-14.01-20.54-5.86c-14.66-4.18-17.92-13.51-18.03-28.62-4.79,7.38-8.39,15.46-7.25,23.95s8.85,16.01,17.06,18.67c4.67,1.52,9.15,3.58,13.12,5.87ZM145.75,77.12l11.29-5.8c8.46-2.53,16.82-8.94,18.71-17.91,1.88-8.91-1.51-18.38-7.15-25.49,1.62,14.33-4.29,25.4-17.37,28.86l-21.73,5.74c6.52,5,11.31,8.99,16.25,14.61ZM120.97,128.33c7.92,0,14.64,2.74,20.13,7.48,10.36-13.34,11.88-30.07,5.38-45.15-10.48-24.33-36.56-33.08-61.62-27.5-15.98,3.55-27.73,14.51-33.26,29.72s-3.65,30.27,5.79,42.88c5.28-4.31,11.31-6.88,17.98-7.36l45.61-.06ZM44.64,94.2l4.92-12.27c-8.73-8.24-21.76-7-30.56.62,4.98,9.66,15.86,13.5,25.64,11.65ZM154.29,94.18c10.31,2.35,19.64-1.94,25.6-11.16-8.08-8.32-22.34-9.44-30.35-.31l4.75,11.47ZM123.91,177.32c12.78-.34,20.27-12.38,19.46-23.13-.85-11.28-9.94-19.87-22.04-19.81-16.01.08-31.86-.52-47.6.57-11.41.78-19.1,11.04-18.97,21.12.12,9.39,7.11,20.27,18.06,20.97,16.71,1.07,33.48.74,51.09.28Z" />
      <path d="M72.86,103.91c3.07-.83,6.11,1.57,6.62,4.38s-1.07,6.01-4.03,6.88c-2.43.72-6.07-1.16-6.77-3.25-1.2-3.54.39-6.99,4.18-8.02Z" />
      <path d="M123.36,115.05c-2.81-.96-4.11-4.38-3.61-6.46.68-2.81,3.37-5.34,6.49-4.84,3.4.54,5.04,4.22,4.4,7.33-.48,2.34-3.63,5.21-7.29,3.97Z" />
      <path d="M84.76,155.45l-10.43.29c-1.19.03-3.37-1.6-3.54-2.66-.21-1.26,2.06-3.59,3.4-3.56l10.01.2c.95.02,2.71,1.72,3.09,2.53s-1.55,3.18-2.53,3.21Z" />
      <path d="M124.83,155.53l-11.29.14c-.94.01-2.85-1.79-3.01-2.69-.19-1.07,1.82-3.28,2.96-3.28l9.77-.06c.79,0,2.51,1.25,3.07,1.8.7.69-.49,4.08-1.5,4.09Z" />
    </IllustIcon>
  );
}

/** Cara de ternero */
export function AnimalCalf({ size = 24, color = "currentColor", className = "" }: IconProps) {
  return (
    <IllustIcon size={size} color={color} className={className}>
      <path d="M141.81,71.41c11.93-10.51,31.41-6.26,37.54,6.92-4.52,11.98-17.62,18.11-29.59,16.28.93,11.33-2.34,20.83-8.6,29.63,4.17,8.16,4.65,16.92.58,24.93-5.2,7.73-13.49,12.74-23.03,12.69l-40.81-.18c-7.88-.03-15.48-5.95-19.35-12.08-5.15-8.15-4.49-17.26.04-25.08-6.2-8.74-9.14-18.6-8.79-29.84-12.06,2.28-24.85-3.87-30.19-15.54-2.47-5.4,20.4-22.76,38.41-7.5l6.37-8.16c-7.28-7.43-7.54-17.46-1-24.67l2.64-2.91c.71-.79,3.31.05,4.49,1-.2,7.09,4.1,13.96,12.02,15.87l16.6-3.25-2.02-5.36c1.79-3.22,2.38-4.92,3.51-8.46.17-.1,1.52.11,1.61.63l.51,2.93c.63,3.62.73,6.27.1,10.41l13.14,2.42c5.42,1,12-5.38,11.84-10.5-.08-2.6.86-5.29,2.65-6,1.4-.55,4.27,1.07,5.25,2.49,5.63,8.1,6.33,17.55-.5,25.37l6.56,7.95ZM75.63,55.96c-4.93-3.63-6.73-6.14-9.45-11.2-2.85,5.06-1.92,11.32,2.54,15.6l6.92-4.4ZM130.94,60.22c4.41-4.72,4.95-10.37,2.03-15.6-2.35,5.06-4.33,7.66-8.63,11l6.6,4.6ZM121.3,113.27c5.83,0,11.16,3.67,15.58,6.81,14.14-17.78,6.21-46.6-13.06-57.93-15.22-8.96-34.3-8.28-49.2.91-17.98,11.1-24.92,38.45-12.04,56.87,4.86-3.73,10.25-6.74,16.09-6.73l42.64.07ZM51.12,88.55l3.78-12.05c-8.87-7.88-21.42-6.66-29.51,1.79,5.14,9.42,15.82,13.45,25.72,10.26ZM148.78,88.58c10.15,2.69,19.45-1.69,24.83-10.2-8.02-7.81-20.36-9.85-28.92-1.98l4.09,12.18ZM121.19,119.12l-42.34-.1c-10.45-.02-17.69,8.19-18,17.68-.28,8.71,5.94,19.01,16.28,18.98l46.14-.1c9.78-.02,15.88-10.79,15.54-18.87-.37-8.89-7.21-17.57-17.63-17.59Z" />
      <path d="M76.79,93.73c2.9-.77,5.86,1.82,6.06,4.07.22,2.52-1.46,5.2-4.08,5.98-1.91.57-5.25-1.51-5.79-3.34-.76-2.57.66-5.88,3.81-6.72Z" />
      <path d="M123.14,103.8c-4.05.51-6.27-2.74-5.76-6.03.38-2.46,3.77-5.14,6.67-3.91,1.41.6,2.92,3.49,3.03,4.87.12,1.58-2.05,4.83-3.94,5.07Z" />
      <path d="M87.57,137.15l-9.8.18c-.94.02-2.92-1.76-2.89-2.66s1.95-2.68,2.89-2.67l8.8.12c.64,0,2.07,1.09,2.46,1.59.53.67-.62,3.42-1.47,3.44Z" />
      <path d="M121.59,137.29l-9.69-.08c-.65,0-1.84-1.91-1.78-2.56.08-.87,1.76-2.59,2.63-2.58l8.87.06c.81,0,2.47,1.71,2.54,2.52s-1.69,2.65-2.57,2.64Z" />
    </IllustIcon>
  );
}

/** Corte de carne / steak con hueso */
export function Meat({ size = 24, color = "currentColor", className = "" }: IconProps) {
  return (
    <IllustIcon size={size} color={color} className={className}>
      <path d="M129.58,156.19c-29.68,6.05-76.49,6.02-101.15-10.14-6.41-4.2-10.35-11.05-10.47-18.78l-.35-21.76c-.35-21.9,16.15-23.48,34.62-26.72,13.15-2.3,26.7-8.22,33.81-19.86,7.93-12.98,18.74-21.94,34.39-24.07,30.13-4.1,60.85,16.89,60.74,44.2l-.14,33.94c-.1,24.19-28.31,38.46-51.45,43.19ZM164.95,103.84c10.23-10.13,12.94-24.36,6.92-37.39-9.45-20.48-35.98-30.63-58.4-23.34-19.41,6.32-19.05,21.6-34.15,31.81-9.84,6.66-21.11,9.57-32.85,11.18-11.24,1.54-22.57,5.44-22.2,15.45.9,24.18,105.21,37.43,140.67,2.29ZM24.34,126.72c.31,12.47,11.98,18.24,24,21.41,21.51,5.66,43.31,6.71,65.44,4.26,30.87-3.42,66.27-16.21,60.77-50.93-8.33,11.28-18.64,18.09-31.41,22.38-30.6,10.29-77.46,10.28-107.08-2.37l-12.01-6.98.3,12.23Z" />
      <path d="M143.52,78.71c-7.23,5.01-16.01,4.75-23.15.29-2.71-1.69-4.64-5.45-4.66-8.38-.02-3.4,2.21-6.95,5.28-8.83,10.53-6.43,25.46-2.08,26.94,7.42.49,3.11-1.12,7.21-4.41,9.49ZM141.1,73.08c1.05-1.06.88-4.62-.27-5.48-4.78-3.55-11.73-4.78-16.93-.92-.93.69-2.33,2.84-2.36,3.93s1.44,3.27,2.36,3.86c5.44,3.51,12.16,3.69,17.21-1.4Z" />
    </IllustIcon>
  );
}

/** Vaca preñada con cría visible en la barriga */
export function Pregnant({ size = 24, color = "currentColor", className = "" }: IconProps) {
  return (
    <IllustIcon size={size} color={color} className={className}>
      <path d="M140.28,102.02c13.9,14.13,17.21,34.56,7.88,51.87-8.84,16.4-27.83,25.92-47.05,21.97s-34.66-22.44-34.5-42.98c.09-11.97,5.46-22.12,13.07-30.84-3.63-5.97-4.02-13-.13-19.1-4.45-6.94-6.85-14.85-5.32-23.56-10.19,1.15-22.25-6.31-20.92-14.99,7.68-6.62,18.66-7.86,27.05-1.77-8.24-11.95-2.19-27.46,2.34-25.18,2.89,1.45,1.59,10.12,12.58,14.82,9.81-3.47,19.98-3.81,29.96.08,5.6-2.59,9.83-7.05,10.66-13.6.81-.5,2.53-1.29,3.33-1.19,3.75.44,8.55,15.25.41,25.31,7.67-5.42,16.11-5.68,23.86-.77,1.47.22,4.51,2.52,3.92,4.17-3.11,8.78-11.56,13.72-21.22,13.13,1.6,8.22-.79,16.19-5.68,23.23,3.53,5.96,3.85,12.84-.24,19.4ZM89.83,34.93l-8.42-9.7c-1.02,4.95-.68,10.32,2.91,14.27l5.51-4.58ZM136.42,39.51c3.54-4.81,4.17-9.86,2.41-14.24-2.73,4.34-5.01,6.67-8.74,9.57l6.33,4.67ZM137.13,79.43c5.27-7.16,5.92-14.95,3.93-22.79-3.09-12.17-12.6-20.55-25.07-22.07-8.52-1.04-17.03-.17-24.39,4.7-12.99,8.6-17.33,26.38-8.95,39.99,3.27-1.63,7.04-4.5,10.68-4.53l28.65-.21c5.62-.04,10.98,1.39,15.16,4.91ZM75.32,54.4l2.69-6.94c-5.23-5.68-12.64-4.92-20.07-1,3.09,6.89,10.2,9.97,17.38,7.94ZM144.96,54.48c7.18,1.68,13.47-1.4,17.21-7.54-5.9-4.71-14.63-5.21-19.97.3l2.77,7.24ZM127.87,105.18c5.06-.11,9.04-5.27,10.09-8.51,1.54-4.75.83-9.82-2.97-13.27-2.08-1.9-6.26-4.32-9.31-4.3l-31.78.19c-8.43.05-14.09,8.69-11.98,16.28,1.92,6.91,8.46,10.39,15.31,10.25l30.63-.64ZM137.29,105.16c-5.29,4.01-9.76,4.91-15.39,4.91h-24.65c-5.43,0-10-1.47-14.56-4.72-7.75,8.86-12.16,19.97-11.11,31.38,1.38,14.95,11.23,27.45,23.94,32.5,14.59,5.81,30.11,2.61,41.01-7.85,16.25-15.59,16.15-38.7.75-56.23Z" />
      <path d="M99.95,64.65c-1.05,1.37-3.8,3.08-5.56,3.16-2.08.09-4.77-1.36-6.36-2.96-.58-.58-.36-2.68.23-3.16,2-1.63,4.31,4.69,8.98.81.47-.39,1.88-.69,2.44-.46s.66,2.09.27,2.61Z" />
      <path d="M122.85,62.4c4.37,3.74,9.22-3.08,9.14.21-.06,2.41-2.63,4.58-4.69,5.04s-4.86-.5-6.82-2.06c-.62-.49-1.08-2.48-.6-3.07s2.37-.63,2.97-.11Z" />
      <path d="M101.71,94.26l-7.09.13c-.68.01-2.11-1.27-2.09-1.93s1.41-1.94,2.09-1.93l6.37.09c.46,0,1.49.79,1.78,1.15.38.48-.45,2.48-1.06,2.49Z" />
      <path d="M126.31,94.36l-7.01-.06c-.47,0-1.33-1.38-1.29-1.85.06-.63,1.27-1.87,1.9-1.87l6.42.04c.59,0,1.78,1.23,1.84,1.83s-1.22,1.92-1.86,1.91Z" />
    </IllustIcon>
  );
}

/** Cría con hocico y cuerpo detallado */
export function BabyCalf({ size = 24, color = "currentColor", className = "" }: IconProps) {
  return (
    <IllustIcon size={size} color={color} className={className}>
      <path d="M144.57,111.27c3.44,8.82,5.64,17.45,1.2,27.09-2.97,6.44-10.83,11.92-18.48,15.34-.24,9.06-4.7,17.36-14.35,20.09-7.48,2.12-16.14,2.13-23.88.82-11.33-1.92-16.54-11.18-17.33-21.53-8.2-2.77-15.24-8.5-18.36-15.03-4.28-8.96-2.62-17.77,1.46-25.97-6.46-11.35-11.64-23.67-8.44-37.74-14.42,2.22-25.95-4.91-32.39-17.03l.91-5.63c14.34-8.41,28.32-10.85,40.67-.02,5-6.61,11.43-11.28,18.39-15.27-.28-5.92-.24-12.43,4.91-16.91,1.26-1.1,5.61-1.5,5.62.06,0,2.42.05,5.59.64,7.29.62,1.8,4.78,5,6.38,3.89,5.78-3.99,10.14-3.56,16.11-.68,3.8,1.83,10.33-3.58,11.12-7.15.44-1.99,1.59-3.87,2.68-4.26,4.56-1.63,6.83,8.99,3.69,18.05,7.41,3.52,13.8,8.15,18.71,15.19,10.51-9.75,24.9-9.16,36.53-1.58,1.5.98,3.72,2.96,4.23,4.36,2.26,6.22-15.05,23.55-31.48,20.22,2.64,13.14-1.55,25-8.52,36.39ZM126.41,139.83c-1.25,1.97-1.42,4.6-.33,7.05,7.97-1.54,13.15-7.51,15.55-14.22,2.7-7.54-.71-14.67-4.35-20.76,4.05-7.4,9.32-14.79,10.25-23.54,2.03-18.93-7.72-39.11-24.68-45.6l-4.5,3.32c-.78.58-3.07-1.39-3.56-2.47,5.59-3.21,8.09-9.17,6.6-15.28-3.51,3.82-6.66,5.91-10.72,7.21-4.83,1.55-7.75.77-10.92-4.1l-5.32,4.65c-5.26,2.28-11.6-2.65-14.46-8.18-4.64,14.84,12.79,16.78,10.88,19.05-3.45,4.08-8.71.68-13.25-5.26-11.51,4.18-19.02,14.23-23.06,25.49-5.62,15.63-2.87,31.33,7.94,44.66-4.75,6.12-6.7,12.59-4.93,20.34,2.22,9.75,14.98,15.9,15.7,14.43l1.69-3.44c-4.8-6.29-6.4-15.73-2.15-22.54,4.71-7.55,13.04-7.36,19.76-2.41,2.96,2.18,11.15,2.41,14.12.3,5.81-4.26,11.94-5.62,17.7-.07,5.43,5.24,6.75,13.99,2.06,21.39ZM47.77,68.17l4.34-10.68c-7.54-9.69-20.18-8.34-31.84-1.91,4.99,10.59,16.45,15.07,27.5,12.59ZM151.74,68.3c10.94,2.66,21.1-2.24,26.77-11.76-9.39-7.42-22.37-8.38-31.12.03l4.34,11.73ZM119.83,161.6c3.06-6.59.41-17.39-6.38-20.18l-2.67,5.05c-2.18-1.6-3.74-3.47-3.96-5.25-.19-1.58,1.67-5.29,3.27-5.34,3.95-.12,7.27,1.46,11.07,3.18,3.31-4.29,3.79-11,.99-15.56-2.59-4.21-7.38-4.69-10.97-1.61-3.54,3.03-7.53,3.69-12.25,3.64-10.93-.1-11.94-7.06-17.21-5.93-5.77,1.24-8.51,12.15-3.53,19.46l12.94-3.7.86,9.01c.2,2.09-6.22,2.76-5.82,3.61l1.28,2.68c6.35,2.61,17.37,4,23.57-.73,1.77-1.35,3.82-2.88,5.27-2.46,1.2.35,2.43,3.82,1.95,5.13-.91,2.52-3.87,3.42-6.27,4.04-9.6,2.5-18.2,2.57-27.56-.7-2.64-.92-4.05-2.24-5.15-5.55-1.69,3.53-1.64,8.27.94,12.19,6.68,10.12,34.72,9.59,39.62-.98Z" />
      <path d="M73.01,78.31c3.48-1.97,6.93.16,7.96,2.97.94,2.58-.07,5.97-3.07,7.69-2.08,1.2-5.73-.14-7.24-2.62-1.36-2.23-.53-6.43,2.35-8.05Z" />
      <path d="M122.43,89.28c-2.56-.51-4.56-3.89-4.24-5.92.42-2.72,2.72-5.77,5.6-5.69,3.67.1,5.62,3.46,5.33,6.4-.26,2.65-2.28,6.1-6.68,5.22Z" />
    </IllustIcon>
  );
}

/** Biberón / leche */
export function Bottle({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M9 3h6v3l2 3v10a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V9l2-3V3z" />
      <path d="M9 3v3m6-3v3" />
      <path d="M9 14h6" />
    </LineIcon>
  );
}

// =============================================================================
// FINANZAS
// =============================================================================

export function Money({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="3" />
      <path d="M6 6v12M18 6v12" />
    </LineIcon>
  );
}

export function ChartUp({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <polyline points="3 17 9 11 13 15 21 7" />
      <polyline points="14 7 21 7 21 14" />
    </LineIcon>
  );
}

export function ChartDown({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <polyline points="3 7 9 13 13 9 21 17" />
      <polyline points="14 17 21 17 21 10" />
    </LineIcon>
  );
}

export function ChartBar({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <line x1="2" y1="20" x2="22" y2="20" />
    </LineIcon>
  );
}

export function Bill({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <rect x="2" y="7" width="20" height="10" rx="1" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 7v10M18 7v10" />
    </LineIcon>
  );
}

export function MoneyFlying({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M12 2v20" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      <path d="M19 3l2 2-2 2" />
    </LineIcon>
  );
}

export function CreditCard({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
      <line x1="6" y1="15" x2="10" y2="15" />
    </LineIcon>
  );
}

export function Briefcase({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      <path d="M2 12h20" />
    </LineIcon>
  );
}

export function Scale({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M12 3v18" />
      <path d="M4 20h16" />
      <path d="M5 9l-3 6h6L5 9z" />
      <path d="M19 9l-3 6h6L19 9z" />
      <path d="M5 9h14" />
    </LineIcon>
  );
}

// =============================================================================
// UBICACIONES
// =============================================================================

export function FieldCrop({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M2 22 16 8" />
      <path d="M3.47 12.53 5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94z" />
      <path d="M7.47 8.53 9 7l1.53 1.53a3.5 3.5 0 0 1 0 4.94L9 15l-1.53-1.53a3.5 3.5 0 0 1 0-4.94z" />
      <path d="M11.47 4.53 13 3l1.53 1.53a3.5 3.5 0 0 1 0 4.94L13 11l-1.53-1.53a3.5 3.5 0 0 1 0-4.94z" />
    </LineIcon>
  );
}

export function Landscape({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M2 20l6-10 5 7 3-4 6 7H2z" />
      <circle cx="18" cy="6" r="2" />
    </LineIcon>
  );
}

export function Home({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </LineIcon>
  );
}

export function Logout({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </LineIcon>
  );
}

export function LocationPin({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </LineIcon>
  );
}

export function Map({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </LineIcon>
  );
}

// =============================================================================
// HERRAMIENTAS
// =============================================================================

export function Wrench({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </LineIcon>
  );
}

export function Tools({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      <path d="M2.5 2.5l4 4" />
    </LineIcon>
  );
}

export function Settings({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </LineIcon>
  );
}

export function Fuel({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <line x1="3" y1="22" x2="15" y2="22" />
      <line x1="4" y1="9" x2="14" y2="9" />
      <path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18" />
      <path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2 2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5" />
    </LineIcon>
  );
}

export function CloudRain({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <line x1="16" y1="13" x2="16" y2="21" />
      <line x1="8" y1="13" x2="8" y2="21" />
      <line x1="12" y1="15" x2="12" y2="23" />
      <path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
    </LineIcon>
  );
}

// =============================================================================
// ESTADOS / ALERTAS
// =============================================================================

export function CheckCircle({ size = 24, color = "var(--cm-success)", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </LineIcon>
  );
}

export function XCircle({ size = 24, color = "var(--cm-danger)", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </LineIcon>
  );
}

export function AlertTriangle({ size = 24, color = "var(--cm-warning-ink)", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </LineIcon>
  );
}

export function Info({ size = 24, color = "var(--cm-info-ink)", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </LineIcon>
  );
}

export function CircleGreen({ size = 24, className = "" }: Pick<IconProps, "size" | "className">) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="10" fill="var(--cm-success)" />
    </svg>
  );
}

export function CircleYellow({ size = 24, className = "" }: Pick<IconProps, "size" | "className">) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="10" fill="var(--cm-warning)" />
    </svg>
  );
}

export function CircleRed({ size = 24, className = "" }: Pick<IconProps, "size" | "className">) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="10" fill="var(--cm-danger)" />
    </svg>
  );
}

export function Siren({ size = 24, color = "var(--cm-danger)", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M7 12a5 5 0 0 1 10 0" />
      <path d="M5 20a7 7 0 0 1 14 0" />
      <line x1="12" y1="12" x2="12" y2="7" />
      <rect x="9" y="20" width="6" height="3" rx="1" />
    </LineIcon>
  );
}

// =============================================================================
// UI / ACCIONES
// =============================================================================

export function Edit({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </LineIcon>
  );
}

export function Trash({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6m4-6v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </LineIcon>
  );
}

export function Search({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </LineIcon>
  );
}

export function Lock({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </LineIcon>
  );
}

export function Save({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </LineIcon>
  );
}

export function Clipboard({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="12" y2="16" />
    </LineIcon>
  );
}

export function FileText({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </LineIcon>
  );
}

export function MessageCircle({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </LineIcon>
  );
}

export function Download({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </LineIcon>
  );
}

export function Calendar({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </LineIcon>
  );
}

// =============================================================================
// MISCELÁNEOS
// =============================================================================

export function Lightbulb({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <line x1="9" y1="18" x2="15" y2="18" />
      <line x1="10" y1="22" x2="14" y2="22" />
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
    </LineIcon>
  );
}

export function Rocket({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </LineIcon>
  );
}

export function Bell({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </LineIcon>
  );
}

export function Smartphone({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </LineIcon>
  );
}

export function Sync({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M4.93 19.07a10 10 0 0 1 0-14.14M19.07 4.93a10 10 0 0 1 0 14.14" />
      <path d="M7.76 16.24a6 6 0 0 1 0-8.49M16.24 7.76a6 6 0 0 1 0 8.49" />
      <circle cx="12" cy="12" r="2" />
    </LineIcon>
  );
}

export function Refresh({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </LineIcon>
  );
}

export function Eye({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </LineIcon>
  );
}

export function EyeOff({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </LineIcon>
  );
}

export function Pencil({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </LineIcon>
  );
}

export function Crosshair({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <circle cx="12" cy="12" r="10" />
      <line x1="22" y1="12" x2="18" y2="12" />
      <line x1="6" y1="12" x2="2" y2="12" />
      <line x1="12" y1="6" x2="12" y2="2" />
      <line x1="12" y1="22" x2="12" y2="18" />
    </LineIcon>
  );
}

export function Layers({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </LineIcon>
  );
}

export function MoveVertical({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <polyline points="8 18 12 22 16 18" />
      <line x1="12" y1="2" x2="12" y2="22" />
      <polyline points="8 6 12 2 16 6" />
    </LineIcon>
  );
}

export function Package({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </LineIcon>
  );
}

export function Health({ size = 24, color = "currentColor", className = "", strokeWidth = 2 }: IconProps) {
  return (
    <LineIcon size={size} color={color} className={className} strokeWidth={strokeWidth}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </LineIcon>
  );
}

// =============================================================================
// EXPORT CENTRAL
// =============================================================================

export const IconMap = {
  // Agro / Ganado (ilustrativos)
  AnimalCow,
  AnimalBull,
  AnimalCalf,
  Meat,
  Pregnant,
  BabyCalf,
  Bottle,
  // Finanzas
  Money,
  ChartUp,
  ChartDown,
  ChartBar,
  Bill,
  MoneyFlying,
  CreditCard,
  Briefcase,
  Scale,
  // Ubicaciones
  FieldCrop,
  Landscape,
  Home,
  LocationPin,
  Map,
  // Herramientas
  Wrench,
  Tools,
  Settings,
  Fuel,
  CloudRain,
  // Estados / Alertas
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  CircleGreen,
  CircleYellow,
  CircleRed,
  Siren,
  // UI / Acciones
  Edit,
  Trash,
  Search,
  Lock,
  Logout,
  Save,
  Clipboard,
  FileText,
  MessageCircle,
  Download,
  Calendar,
  // Misceláneos
  Lightbulb,
  Rocket,
  Bell,
  Smartphone,
  Sync,
  Refresh,
  Eye,
  MoveVertical,
  Package,
  Health,
} as const;

export type IconName = keyof typeof IconMap;

/**
 * Componente dinámico — renderiza cualquier ícono por nombre.
 * @example <DynamicIcon name="AnimalCow" size={32} />
 */
export function DynamicIcon({ name, ...props }: { name: IconName } & IconProps) {
  const Component = IconMap[name] as React.FC<IconProps>;
  return <Component {...props} />;
}
